<template>
  <table class="entsys--table">
    <thead>
      <tr class="hidden-md-and-down">
        <th
          v-for="header in headersProp"
          :key="header.value"
          :class="`text-${header.align}`"
        >
          {{ header.text }}
        </th>
      </tr>
    </thead>
    <tbody>
      <tr v-show="loadingState || items.length == 0" class="pa-0">
        <td colspan="99" class="pa-0">
          <v-progress-linear
            :active="loadingState"
            color="secondary"
            indeterminate
          />

          <div
            v-if="loadingState && items.length == 0"
            class="text-center py-4 muted--text"
          >
            {{ $t("common:loading.messsage", "Loading...", "Loading message") }}
          </div>
          <div
            v-else-if="!loadingState && items.length == 0"
            class="text-center py-4 muted--text"
          >
            {{ $t("events:noEventsFound", "No events found") }}
          </div>
        </td>
      </tr>
      <template v-for="(item, index) in items">
        <template v-if="$vuetify.breakpoint.mdAndUp">
          <router-link
            :key="'-row-' + item.id"
            :to="linkToEvent(item)"
            class="no-link-hover"
            style="display: contents"
            :target="embeddedMode ? '_blank' : undefined"
          >
            <tr :data-scroll-focus="item.id">
              <td>
                <div>
                  {{ eventDate(item) }}
                  <AppShowEventReferenceCode :event="item"
                    ><template #prepend>
                      |
                    </template></AppShowEventReferenceCode
                  >
                </div>
                <div>
                  <AppShowCountry :country="item.country" />
                  <span
                    :class="
                      item.venueAddress && mapModal ? 'place-modal-link' : ''
                    "
                    :role="item.venueAddress && mapModal ? 'button' : ''"
                    @click.prevent="
                      item.venueAddress && mapModal
                        ? $emit('placeDetails', item)
                        : null
                    "
                  >
                    {{ get(item.region, "name", "") }},
                    {{ item.place }}
                  </span>
                </div>
              </td>
              <td>
                <div>
                  <b>{{ item.name }}</b>
                  <v-icon
                    v-if="item.isHighlighted"
                    small
                    class="ml-2"
                    color="star"
                    >$iconStar</v-icon
                  >
                </div>
                <div>
                  <AppShowEventCupComponent :event="item" name-short />
                </div>
                <div>
                  <span class="grey--text"
                    >{{ item.organiser.name }}
                    <template v-if="item.hostOrganisation">
                      | {{ item.hostOrganisation.name }}</template
                    ></span
                  >
                </div>
              </td>
              <td>
                <AppShowDisciplineName
                  v-if="item.discipline"
                  :name="item.discipline.name"
                  chip
                />
              </td>
              <td style="width: 0">
                <span class="secondary--text">
                  <AppShowEventCategories
                    :event-categories="item.eventCategories"
                  />
                  <AppShowCompetitionLevelNames
                    :value="item.competitionLevels"
                  />
                </span>
              </td>
              <td>
                <slot name="action" v-bind="item">
                  <AppShowEventStatus :event="item" border />
                </slot>
              </td>
            </tr>
          </router-link>

          <tr
            v-if="showBannerConditionally(index, 'events')"
            :key="'-ad-' + item.id"
            class="no-hover d-print-none"
          >
            <td colspan="99" class="pa-0">
              <AppShowBanners placement-name="CALENDAR" />
            </td>
          </tr>
        </template>
        <template v-else>
          <router-link
            :key="'-mobile-row-' + item.id"
            :to="linkToEvent(item)"
            class="no-link-hover"
            style="display: contents"
            :target="embeddedMode ? '_blank' : undefined"
          >
            <tr :data-scroll-focus="item.id" class="no-hover" style="border: 0">
              <td
                colspan="99"
                class="pa-0 py-1"
                style="position: relative; height: auto"
              >
                <div>
                  <div
                    :class="[
                      disciplineColor(item),
                      'event-vertical-card--discipline no-after--discipline'
                    ]"
                  >
                    <div class="d-flex flex-row">
                      <div style="text-transform: capitalize">
                        {{ disciplineDisplayName(item)
                        }}<AppShowEventReferenceCode
                          :event="item"
                          force-current-color
                          ><template #prepend>
                            |
                          </template></AppShowEventReferenceCode
                        >
                      </div>
                      <div>
                        <AppShowEventStatus
                          :event="item"
                          no-button
                          block
                          always-show
                        />
                      </div>
                    </div>
                  </div>

                  <section class="pa-2 event-vertical-card--content">
                    <v-row no-gutters class="flex-row">
                      <v-col class="logo-mobile">
                        <OrganizationLogo :organiser="item.organiser" />
                      </v-col>
                      <v-col class="pl-1">
                        <div style="font-size: 12px">
                          {{ eventDate(item) }} &nbsp;|&nbsp;
                          <AppShowCountry :country="item.country" />
                          <span
                            :class="
                              item.venueAddress && mapModal
                                ? 'place-modal-link'
                                : ''
                            "
                            @click.prevent="
                              item.venueAddress && mapModal
                                ? $emit('placeDetails', item)
                                : null
                            "
                          >
                            {{
                              $federation.isFinnishFederation
                                ? get(item, "region.name", "") + " ,"
                                : ""
                            }}
                            {{ item.place }}
                          </span>
                        </div>
                        <div class="no-wrap" style="font-size: 14px">
                          <b>{{ item.name }}</b>
                          <v-icon
                            v-if="item.isHighlighted"
                            style="font-size: 12px"
                            class="ml-1"
                            color="star"
                            >$iconStar</v-icon
                          >
                        </div>

                        <div>
                          <span class="grey--text"
                            >{{ item.organiser.name }}
                            <template v-if="item.hostOrganisation">
                              | {{ item.hostOrganisation.name }}</template
                            ></span
                          >
                        </div>
                        <div class="secondary--text" style="font-size: 12px">
                          <div class="d-flex no-wrap">
                            <div v-if="item.noncompetition">
                              <AppShowEventCategories
                                :event-categories="item.eventCategories"
                              />

                              <AppShowProfQualificationsList
                                :prof-qualifications="item.profQualifications"
                                cut-off-list
                                class="mt-1"
                              />
                            </div>
                            <AppShowCompetitionLevelNames
                              :value="item.competitionLevels"
                            />
                            <template
                              v-if="
                                item.competitions && item.competitions.length
                              "
                            >
                              <span class="px-1">|</span>
                              <AppShowDifficultyLevels
                                range
                                :value="
                                  item.competitions.map(c => c.difficultyLevel)
                                "
                              />
                            </template>
                          </div>
                          <div class="no-wrap-parent">
                            <AppShowEventcodes :value="item.competitions" />
                            <AppShowEventCupComponent
                              v-if="item.cups && item.cups.length > 0"
                              :event="item"
                            />
                          </div>
                          <div
                            v-if="$scopedSlots.action"
                            class="d-flex justify-center"
                          >
                            <slot name="action" v-bind="item" />
                          </div>
                        </div>
                      </v-col>
                    </v-row>
                  </section>
                </div>
              </td>
            </tr>
          </router-link>

          <tr
            v-if="showBannerConditionally(index, 'events')"
            :key="'-mobile-ad-' + item.id"
            class="no-hover pa-0"
            style="border: 0"
          >
            <td colspan="99" class="pa-0">
              <AppShowBanners
                placement-name="CALENDAR"
                :show-only-position-number="1"
              />
            </td>
          </tr>
        </template>
      </template>
    </tbody>
  </table>
</template>
<script>
import { inject } from "vue";
import gql from "fraql";
import { get } from "lodash-es";
import { useBannerHelpers } from "@composables/banner_helpers";
import { useDisciplineHelper } from "@composables/masterdata/competitions/disciplines";
import AppShowEventStatus from "@/components/elements/AppShows/event/AppShowEventStatus.vue";
import AppShowEventCupComponent from "@/components/elements/AppShows/event/AppShowEventCupComponent.vue";
import AppShowCompetitionLevelNames from "@/components/elements/AppShows/event/AppShowCompetitionLevelNames.vue";
import AppShowDifficultyLevels from "@/components/elements/AppShows/AppShowDifficultyLevels.vue";
import AppShowDisciplineName from "@/components/elements/AppShows/AppShowDisciplineName.vue";
import AppShowCountry from "@AppShows/AppShowCountry.vue";
import AppShowBanners from "@/components/elements/banners/AppShowBanners.vue";
import AppShowEventCategories from "@AppShows/event/AppShowEventCategories.vue";
import OrganizationLogo from "@/components/elements/AppOrganLogo.vue";
import AppShowEventcodes from "@/components/elements/AppShows/event/AppShowEventcodes.vue";
import AppShowProfQualificationsList from "@AppShows/AppShowProfQualificationsList.vue";
import AppShowEventReferenceCode from "@AppShows/event/AppShowEventReferenceCode.vue";

export default {
  name: "EventsTemplateEvents",
  components: {
    AppShowEventStatus,
    AppShowEventCupComponent,
    AppShowCompetitionLevelNames,
    AppShowDifficultyLevels,
    AppShowDisciplineName,
    AppShowBanners,
    AppShowEventcodes,
    OrganizationLogo,
    AppShowCountry,
    AppShowEventCategories,
    AppShowProfQualificationsList,
    AppShowEventReferenceCode
  },
  provide: {
    registrationBtnForceClick: true
  },
  inject: {
    isNoncompetition: {
      default: false
    }
  },
  props: {
    hideActions: {
      type: Boolean,
      default: true
    },
    items: {
      type: Array,
      default: () => []
    },
    loading: {
      type: [Boolean, Function],
      default: false
    },
    showAds: {
      type: Boolean,
      default: false
    },
    mapModal: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const embeddedMode = inject("embeddedMode", false);
    const { showBannerConditionally } = useBannerHelpers();
    return { embeddedMode, ...useDisciplineHelper(), showBannerConditionally };
  },
  fragments: {
    eventsNoCompetitions: gql`
      fragment _ on Event {
        id
        name
        ${AppShowEventReferenceCode.fragments.eventFields}
        ${AppShowEventStatus.fragments.eventStatus}
        startDate
        endDate
        registrationEndDate
        description
        alertText
        isHighlighted
        noncompetition
        eventCategoryIds
        eventCategories @client {
          ${AppShowEventCategories.fragments.eventCategoryFields}
        }
        profQualificationIds
        profQualifications @client {
          ${AppShowProfQualificationsList.fragments.profQualificationFields}
        }
        disciplineId
        discipline @client {
          id
          name
        }
        ${AppShowEventCupComponent.fragments.cups}
        countryId
        country @client {
          id
          name
          alpha2
          ioc
        }
        organiser {
          id
          name
          fullName
          logoUrl
        }
        ${AppShowCompetitionLevelNames.fragments.competitionLevels}
        difficultyGroups {
          id
          name
          nameShort
        }
        regionId
        region @client {
          id
          name
        }
        place
      }
    `,
    events: gql`
      fragment _ on Event {
        id
        name
        ${AppShowEventReferenceCode.fragments.eventFields}
        ${AppShowEventStatus.fragments.eventStatus}
        startDate
        endDate
        registrationEndDate
        description
        alertText
        isHighlighted
        noncompetition
        eventCategoryIds
        eventCategories @client {
          ${AppShowEventCategories.fragments.eventCategoryFields}
        }
        disciplineId
        discipline @client {
          id
          name
        }
        ${AppShowEventCupComponent.fragments.cups}
        countryId
        country @client {
          id
          name
          alpha2
          ioc
        }
        organiser {
          id
          name
          fullName
          logoUrl
        }
        ${AppShowCompetitionLevelNames.fragments.competitionLevels}
        difficultyGroups {
          id
          name
          nameShort
        }
        regionId
        region @client {
          id
          name
        }
        place
        competitions {
          id
          eventId
          code
          startDate
          eventcodeId
          eventcode @client {
            id
            name
          }
          difficultyLevelId
          difficultyLevel @client {
            id
            displayName
          }
          competitionRuleId
          competitionRule @client {
            id
            name
          }
          competitionLevelId
          competitionLevel @client {
            id
            name
          }
          ${AppShowEventCupComponent.fragments.cupSeason}
        }
      }
    `,
    compactEvents: gql`
      fragment _ on Event {
        id
        name
        status
        startDate
        endDate
        isHighlighted
        registrationEndDate
        ${AppShowEventReferenceCode.fragments.eventFields}
        ${AppShowEventCupComponent.fragments.cups}
        noncompetition
        eventCategoryIds
        eventCategories @client {
          ${AppShowEventCategories.fragments.eventCategoryFields}
        }
        disciplineId
        discipline @client {
          id
          name
        }
        countryId
        country @client {
          id
          name
          alpha2
          ioc
        }
        regionId
        region @client {
          id
          name
        }
        place
        organiser {
          id
          name
          fullName
          logoPath
        }
        hostOrganisation {
          id
          name
        }
        competitionLevels {
          id
          name
          rank
        }
        competitions {
          id
          eventcodeId
          eventcode @client {
            id
            name
          }
          ${AppShowEventCupComponent.fragments.cupSeason}
        }
      }
    `
  },
  computed: {
    get: () => get,
    loadingState() {
      if (typeof this.loading === "boolean") return this.loading;
      else return this.loading();
    },
    headersProp() {
      const ncevent = this.isNoncompetition;
      if (ncevent) {
        return [
          {
            value: "date",
            text: this.$t(
              "calendar:tableviewNonComp.DateLocation.column",
              "Date / Location",
              "Date and location column name in calendar table view"
            ),
            sortable: false,
            align: "left"
          },
          {
            value: "name",
            text: this.$t(
              "calendar:tableviewNonComp.EventOrganiser.column",
              "Event / Organiser",
              "Event and organiser column name in calendar table view"
            ),
            sortable: false,
            align: "left"
          },
          {
            value: "type",
            text: this.$t("calendar:tableviewNonComp.Type.column", "Type"),
            sortable: false,
            align: "left"
          },
          {
            value: "levels",
            text: this.$t(
              "calendar:tableviewNonComp.category.column",
              "Categories"
            ),
            sortable: false,
            align: "left"
          },
          {
            value: "status",
            text: !this.$scopedSlots.action
              ? this.$t(
                  "calendar:tableviewNonComp.EventStatus.column",
                  "Status",
                  "Event status column name in calendar table view"
                )
              : "",
            sortable: false,
            align: "left"
          }
        ];
      }
      return [
        {
          value: "date",
          text: this.$t(
            "calendar:tableview.DateLocation.column",
            "Date / Location",
            "Date and location column name in calendar table view"
          ),
          sortable: false,
          align: "left"
        },
        {
          value: "name",
          text: this.$t(
            "calendar:tableview.EventOrganiser.column",
            "Event / Organiser",
            "Event and organiser column name in calendar table view"
          ),
          sortable: false,
          align: "left"
        },
        {
          value: "discipline",
          text: this.$t(
            "calendar:tableview.Discipline.column",
            "Discipline",
            "Discipline column name in calendar table view"
          ),
          sortable: false,
          align: "left"
        },
        {
          value: "levels",
          text: this.$t(
            "calendar:tableview.CompetitionLevels.column",
            "Competition levels",
            "Competition levels column name in calendar table view"
          ),
          sortable: false,
          align: "left"
        },
        {
          value: "status",
          text: !this.$scopedSlots.action
            ? this.$t(
                "calendar:tableview.EventStatus.column",
                "Status",
                "Event status column name in calendar table view"
              )
            : "",
          sortable: false,
          align: "left"
        }
      ];
    }
  },
  methods: {
    disciplineDisplayName(event) {
      if (event.discipline) {
        return this.$t("common:discipline." + event.discipline.name);
      } else if (event.disciplineId) {
        return this.$t(
          "common:discipline." +
            this.getDisciplineFromMaster(event.disciplineId, "name")
        );
      } else {
        return null;
      }
    },
    disciplineColor(item) {
      if (item.discipline) {
        return item.discipline.name;
      } else {
        return (
          this.getDisciplineFromMaster(item.disciplineId, "name") ||
          "showjumping"
        );
      }
    },
    eventLink(event) {
      if (["results_published", "closed", "archived"].includes(event.status)) {
        return "results";
      } else if (event.status == "registration_open") {
        return "registration";
      } else {
        return "eventPage";
      }
    },
    linkToEvent(item) {
      if (this.isNoncompetition) {
        return {
          name: "NoncompetitionEventProfilePage",
          params: { eventId: item.id }
        };
      }
      if (["results_published", "closed", "archived"].includes(item.status)) {
        return { name: "EventResultsPage", params: { eventId: item.id } };
      } else {
        return { name: "EventProfilePage", params: { eventId: item.id } };
      }
    },
    eventDate(item) {
      return this.$dayjs.nicerDate(item.startDate, item.endDate);
    }
  }
};
</script>

<style lang="scss">
.link-icon {
  font-size: 50px;
  opacity: 0.25;
  position: absolute;
  bottom: 24px;
  right: 25px;
  color: gray !important;
}
.no-overflow-x .v-data-table__wrapper {
  overflow-x: hidden !important;
}
.logo-mobile {
  max-width: 60px;
  .club-logo img {
    width: 40px !important;

    margin: 10px;
  }
}
.no-after--discipline::after {
  content: none !important;
}

.no-wrap {
  white-space: nowrap;
  text-overflow: ellipsis;
  width: 272px;
  overflow: hidden;
}
.no-wrap-parent > div {
  white-space: nowrap;
  text-overflow: ellipsis;
  width: 272px;
  overflow: hidden;
}
</style>
<style scoped lang="scss">
@media print {
  table.entsys--table {
    thead tr th {
      font-size: 80% !important;
      font-weight: normal !important;
    }
    tbody tr td {
      font-size: 80% !important;
      .chip {
        background-color: transparent !important;
        color: black !important;
      }
      .flag-icon {
        display: none !important;
      }
      .place-modal-link,
      .secondary--text,
      .grey--text {
        color: black !important;
      }
    }
  }
}
</style>
